import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, CATEGORY_NAME } from "@/lib/api";
import { Users, ArrowRight, Pencil, Check, X } from "lucide-react";

export default function Riders() {
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [renamingName, setRenamingName] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/riders").then((r) => setRiders(r.data.riders)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = q
    ? riders.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()))
    : riders;

  const startRename = (name) => {
    setRenamingName(name);
    setDraftName(name);
  };

  const saveRename = async () => {
    const newName = (draftName || "").trim();
    if (!newName || newName === renamingName) {
      setRenamingName(null);
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/rider/${encodeURIComponent(renamingName)}/rename`, { new_name: newName });
      toast.success(`Renamed · updated ${r.data.clips_updated} clip(s)`);
      setRenamingName(null);
      load();
    } catch (err) {
      toast.error("Rename failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1300px]" data-testid="riders-page">
      <div className="border-l-4 border-orange-600 pl-5 mb-8">
        <div className="label-tiny mb-2">Riders & Horses</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Riders</h1>
        <p className="text-stone-400 mt-3">Every named rider from your clips. Click the name to open the profile, the pencil to rename across every clip and cut.</p>
      </div>

      <input
        value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Filter by name…"
        className="field-input !w-full !max-w-md mb-6"
        data-testid="rider-filter"
      />

      {loading ? (
        <div className="text-stone-500 py-12 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-16 text-center" data-testid="riders-empty">
          <Users size={32} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">No named riders yet.</p>
          <p className="text-stone-500 text-sm mt-2">Add rider names when you create cuts to populate this list.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="riders-grid">
          {filtered.map((r) => {
            const isRenaming = renamingName === r.name;
            return (
              <div
                key={r.name}
                className="border border-stone-800 hover:border-orange-700 p-4 transition-colors group"
                data-testid={`rider-${r.name}`}
              >
                <div className="flex items-start justify-between mb-2 gap-2">
                  {isRenaming ? (
                    <input
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveRename();
                        if (e.key === "Escape") setRenamingName(null);
                      }}
                      autoFocus
                      className="field-input !py-1 !text-base flex-1"
                      data-testid={`rider-rename-input-${r.name}`}
                    />
                  ) : (
                    <button
                      onClick={() => navigate(`/rider/${encodeURIComponent(r.name)}`)}
                      className="font-display uppercase font-bold text-xl truncate flex-1 text-left hover:text-orange-400 transition-colors"
                    >
                      {r.name}
                    </button>
                  )}
                  {isRenaming ? (
                    <div className="flex gap-1">
                      <button
                        onClick={saveRename}
                        disabled={saving}
                        className="p-1 text-emerald-400 hover:text-emerald-300 disabled:opacity-40"
                        title="Save rename"
                        data-testid={`rider-rename-save-${r.name}`}
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => setRenamingName(null)}
                        className="p-1 text-stone-500 hover:text-rose-400"
                        title="Cancel"
                        data-testid={`rider-rename-cancel-${r.name}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => startRename(r.name)}
                        className="p-1 text-stone-500 hover:text-orange-400"
                        title="Rename"
                        data-testid={`rider-rename-${r.name}`}
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => navigate(`/rider/${encodeURIComponent(r.name)}`)}
                        className="p-1 text-stone-500 hover:text-orange-400"
                        title="Open profile"
                      >
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  )}
                </div>
                {!isRenaming && (
                  <>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(r.categories || []).map((c) => (
                        <span key={c} className="tag-cat">{CATEGORY_NAME[c] || c}</span>
                      ))}
                    </div>
                    <div className="font-mono-tc text-xs text-stone-500">
                      <span className="text-orange-500 text-base">{r.clip_count}</span> clip{r.clip_count !== 1 ? "s" : ""}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
